/**
 * Bypass Onboarding Modal
 * Allows super_users to mark onboarding as completed for any user
 */
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import UserService from '../../services/user.service';
import { toastSuccess, toastError } from '../../features/clu/utils/notify';

const BypassOnboardingModal = ({ isOpen, onClose, onSuccess, defaultEmail = '' }) => {
  const { t } = useTranslation('common');
  const [email, setEmail] = useState(defaultEmail);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setEmail(defaultEmail || '');
      setError('');
      setLoading(false);
      // Focus input after modal opens
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen, defaultEmail]);

  if (!isOpen) return null;

  const validateEmail = (emailValue) => {
    if (!emailValue || !emailValue.trim()) {
      return t('bypass_onboarding_email_required');
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailValue.trim())) {
      return t('bypass_onboarding_email_invalid');
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateEmail(email);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await UserService.completeUserOnboarding(email.trim());
      
      if (result.success) {
        toastSuccess(
          result.was_already_completed
            ? t('bypass_onboarding_already_completed', { email })
            : t('bypass_onboarding_success', { email })
        );
        
        if (onSuccess) {
          onSuccess(result);
        }
        
        onClose();
        setEmail('');
      } else {
        throw new Error(result.message || t('bypass_onboarding_failed'));
      }
    } catch (err) {
      const errorMessage = err.message || t('bypass_onboarding_error');
      setError(errorMessage);
      toastError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div 
        className="relative bg-surface rounded-2xl shadow-2xl w-full max-w-md transform transition-all"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="p-6 border-b border-line">
          <h3 className="text-2xl font-bold text-ink">
            {t('bypass_onboarding_title')}
          </h3>
          <p className="text-sm text-ink-muted mt-2">
            {t('bypass_onboarding_description')}
          </p>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-6">
            <label 
              htmlFor="user-email" 
              className="block text-sm font-medium text-ink mb-2"
            >
              {t('bypass_onboarding_user_email')} <span className="text-red-500 dark:text-red-400">*</span>
            </label>
            <input
              ref={inputRef}
              id="user-email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError(''); // Clear error on input
              }}
              placeholder="user@example.com"
              disabled={loading}
              className={`w-full px-4 py-3 bg-surface border rounded-xl
                focus:outline-none focus:ring-2 focus:ring-offset-0
                transition-all duration-200 placeholder-gray-400 text-ink
                disabled:bg-surface-2 disabled:cursor-not-allowed
                ${error 
                  ? 'border-red-300 dark:border-red-600 focus:ring-red-500 focus:border-red-500' 
                  : 'border-line focus:ring-blue-500 focus:border-blue-500'
                }`}
            />
            {error && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-300">
                {error}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex space-x-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-6 py-3 bg-surface-2 text-ink rounded-lg font-medium hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('bypass_onboarding_cancel')}
            </button>
            <button
              type="submit"
              disabled={loading || !email.trim()}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('bypass_onboarding_processing')}
                </span>
              ) : (
                t('bypass_onboarding_submit')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BypassOnboardingModal;
